import { type LocationListingQuery, type AssetsListingQuery } from 'cms/cms';
import { resolveImageReferenceToImage } from './imageResolver';
import type { ContentModelReference, ContentModelReferenceSearch, Country } from 'cms/cms-types';
import type { MapItem } from '4-sections/FilteredListings/AssetsListing/Map/ListingMap';
import { resolveCategoriesToMapCategory } from './mapCategoryResolver';
import { type LocationMapItem } from '4-sections/FilteredListings/MapAndListing/Map/ListingMap';
import { generateDescription, type LocationCardResolverSettings } from './locationCardResolver';

export type AssetPage = NonNullable<AssetsListingQuery['AssetPage']>;
export type AssetPageItems = NonNullable<AssetPage['items']>;

export const resolveAssetPagesToMapItems = (items: AssetPageItems): MapItem[] => {
    return items.map(item => {
        // // the autogenerated produce | null for each item, need to remove the | null type
        const {
            ContentLink,
            Title,
            PreviewTitle,
            PreviewDescription,
            PreviewImage,
            AssetTypeCategories,
            SectorCategories,
            Latitude,
            Longitude,
            CountryItems,
            // Countries
        } = item as NonNullable<typeof item>;
        const id = String((ContentLink as NonNullable<typeof ContentLink>).Id);
        const imgSrcSets = [480, 767];
        const img = resolveImageReferenceToImage(PreviewImage as ContentModelReference, '?width=340', imgSrcSets);

        const assetType = resolveCategoriesToMapCategory(AssetTypeCategories)?.description || '';
        const assetTypeName = resolveCategoriesToMapCategory(AssetTypeCategories)?.name || '';
        const sector = resolveCategoriesToMapCategory(SectorCategories)?.description || '';

        const coordinates = {
            lat: Number(Latitude),
            lon: Number(Longitude),
        };

        const countries = CountryItems?.map(countryItem => {
            const { Name, PlaceId } = (((countryItem as NonNullable<typeof countryItem>).ContentLink as ContentModelReferenceSearch).Expanded as Country);
            return {
                name: Name || '',
                placeId: PlaceId || '',
            };
        }) || [];

        return {
            id,
            name: PreviewTitle || Title as string,
            description: PreviewDescription || '',
            ...(img && { img }),
            assetType,
            assetTypeName,
            sector,
            coordinates,
            countries,
            href: ContentLink?.Url ? ContentLink.Url : '',
        };
    });
};

export type LocationPage = NonNullable<LocationListingQuery['LocationPage']>;
export type LocationPageItems = NonNullable<LocationPage['items']>;

export const resolveLocationPagesToMapItems = (items: LocationPageItems, settings: LocationCardResolverSettings): LocationMapItem[] => {
    return items.map(item => {
        // // the autogenerated produce | null for each item, need to remove the | null type
        const {
            ContentLink,
            Title,
            PreviewTitle,
            Address,
            PhoneNumber,
            EmailAddress,
            PreviewImage,
            Latitude,
            Longitude,
            // Countries
        } = item as NonNullable<typeof item>;
        const { emailLabel, telLabel } = settings;
        const id = String((ContentLink as NonNullable<typeof ContentLink>).Id);
        const imgSrcSets = [480, 767];
        const img = resolveImageReferenceToImage(PreviewImage as ContentModelReference, '?width=340', imgSrcSets);

        const coordinates = {
            lat: Number(Latitude),
            lon: Number(Longitude),
        };

        const description = generateDescription({ Address, EmailAddress, PhoneNumber, emailLabel, telLabel });

        return {
            id,
            name: PreviewTitle || Title as string,
            description,
            ...(img && { img }),
            coordinates,
            href: ContentLink?.Url ? ContentLink.Url : '',
        };
    });
};
